import type { ImpersonationState } from '@platewell/schema';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { sessionIsAdmin, type Session } from './api.js';

interface SessionValue {
  session: Session;
  /** True once the server has confirmed this session, rather than it coming from the cache. */
  verified: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({
  session,
  verified,
  children,
}: {
  session: Session;
  verified: boolean;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ session, verified }), [session, verified]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (value == null) throw new Error('useSession must be used inside a SessionProvider');
  return value.session;
}

/**
 * The admin behind the session, or null when the user is simply themselves.
 *
 * Only ever read from a *verified* session. A cached one can say "still impersonating"
 * about a grant the server ended an hour ago, and a banner claiming to be someone else
 * when it is not is worse than a banner that appears a moment late.
 */
export function useImpersonation(): ImpersonationState | null {
  const value = useContext(SessionContext);
  if (value == null) throw new Error('useImpersonation must be used inside a SessionProvider');
  return value.verified ? (value.session.impersonation ?? null) : null;
}

export interface AdminAccess {
  canAdmin: boolean;
  /**
   * False only while the answer is genuinely unknown.
   *
   * The app boots from the session cached in localStorage, and a session cached before
   * permissions existed carries no `permissions` key at all. Without this, a real admin
   * opening a deep link cold would be told they had no access before the server replied.
   */
  resolved: boolean;
}

export function useAdminAccess(): AdminAccess {
  const value = useContext(SessionContext);
  if (value == null) throw new Error('useAdminAccess must be used inside a SessionProvider');
  return {
    canAdmin: sessionIsAdmin(value.session),
    resolved: value.verified || value.session.user.permissions !== undefined,
  };
}
